import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import CryptoJS from 'crypto-js'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'
import { addToHistory } from '../utils/history'

type HashAlgorithm = 'MD5' | 'SHA1' | 'SHA256' | 'SHA512' | 'SHA3'
type OutputFormat = 'hex' | 'base64'
type InputType = 'text' | 'file'

interface HashResult {
  algorithm: HashAlgorithm
  hash: string
}

function HashGenerator() {
  const { t } = useTranslation()
  const [input, setInput] = useState('')
  const [inputType, setInputType] = useState<InputType>('text')
  const [selectedAlgorithms, setSelectedAlgorithms] = useState<HashAlgorithm[]>(['MD5', 'SHA256'])
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('hex')
  const [results, setResults] = useState<HashResult[]>([])
  const [fileName, setFileName] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const saveToHistory = useCallback((inputVal: string, outputVal: string) => {
    if (inputVal && outputVal) {
      addToHistory({
        tool: 'hash-generator',
        toolName: 'Hash Generator',
        input: inputVal.substring(0, 500),
        output: outputVal.substring(0, 500),
      })
    }
  }, [])

  const algorithms: { value: HashAlgorithm; label: string; bits: number }[] = [
    { value: 'MD5', label: 'MD5', bits: 128 },
    { value: 'SHA1', label: 'SHA-1', bits: 160 },
    { value: 'SHA256', label: 'SHA-256', bits: 256 },
    { value: 'SHA512', label: 'SHA-512', bits: 512 },
    { value: 'SHA3', label: 'SHA-3 (256)', bits: 256 },
  ]

  const computeHash = (data: string | CryptoJS.lib.WordArray, algorithm: HashAlgorithm): string => {
    let hash: CryptoJS.lib.WordArray

    switch (algorithm) {
      case 'MD5':
        hash = CryptoJS.MD5(data)
        break
      case 'SHA1':
        hash = CryptoJS.SHA1(data)
        break
      case 'SHA256':
        hash = CryptoJS.SHA256(data)
        break
      case 'SHA512':
        hash = CryptoJS.SHA512(data)
        break
      case 'SHA3':
        hash = CryptoJS.SHA3(data, { outputLength: 256 })
        break
      default:
        hash = CryptoJS.SHA256(data)
    }

    if (outputFormat === 'base64') {
      return hash.toString(CryptoJS.enc.Base64)
    }
    return hash.toString(CryptoJS.enc.Hex)
  }

  const generateHashes = (data: string | CryptoJS.lib.WordArray, inputText?: string) => {
    const newResults: HashResult[] = selectedAlgorithms.map(algorithm => ({
      algorithm,
      hash: computeHash(data, algorithm)
    }))
    setResults(newResults)
    
    // Save to history
    if (newResults.length > 0 && inputText) {
      const outputStr = newResults.map(r => `${r.algorithm}: ${r.hash}`).join('\n')
      saveToHistory(inputText, outputStr)
    }
  }

  const handleTextChange = (text: string) => {
    setInput(text)
    if (text && selectedAlgorithms.length > 0) {
      generateHashes(text, text)
    } else {
      setResults([])
    }
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsProcessing(true)
    setFileName(file.name)
    setInput('')

    try {
      const arrayBuffer = await file.arrayBuffer()
      const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer as unknown as number[])
      generateHashes(wordArray)
    } catch (err) {
      console.error('File reading error:', err)
    } finally {
      setIsProcessing(false)
    }
  }

  const toggleAlgorithm = (algorithm: HashAlgorithm) => {
    setSelectedAlgorithms(prev => {
      const newSelection = prev.includes(algorithm)
        ? prev.filter(a => a !== algorithm)
        : [...prev, algorithm]
      
      // Regenerate hashes with new selection
      if ((input || fileName) && newSelection.length > 0) {
        setTimeout(() => {
          if (inputType === 'text' && input) {
            const newResults: HashResult[] = newSelection.map(algo => ({
              algorithm: algo,
              hash: computeHash(input, algo)
            }))
            setResults(newResults)
          }
        }, 0)
      } else {
        setResults([])
      }
      
      return newSelection
    })
  }

  const handleFormatChange = (format: OutputFormat) => {
    setOutputFormat(format)
    // Regenerate with new format
    if (input && selectedAlgorithms.length > 0) {
      setTimeout(() => {
        const newResults: HashResult[] = selectedAlgorithms.map(algorithm => ({
          algorithm,
          hash: (() => {
            let hash: CryptoJS.lib.WordArray
            switch (algorithm) {
              case 'MD5': hash = CryptoJS.MD5(input); break
              case 'SHA1': hash = CryptoJS.SHA1(input); break
              case 'SHA256': hash = CryptoJS.SHA256(input); break
              case 'SHA512': hash = CryptoJS.SHA512(input); break
              case 'SHA3': hash = CryptoJS.SHA3(input, { outputLength: 256 }); break
              default: hash = CryptoJS.SHA256(input)
            }
            return format === 'base64' ? hash.toString(CryptoJS.enc.Base64) : hash.toString(CryptoJS.enc.Hex)
          })()
        }))
        setResults(newResults)
      }, 0)
    }
  }

  const handleClear = () => {
    setInput('')
    setResults([])
    setFileName('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const selectAllAlgorithms = () => {
    setSelectedAlgorithms(algorithms.map(a => a.value))
    if (input) {
      setTimeout(() => generateHashes(input), 0)
    }
  }

  const getAllHashesText = () => {
    return results.map(r => `${r.algorithm}: ${r.hash}`).join('\n')
  }

  return (
    <Layout showBackButton title={t('toolPages.hashGenerator.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.hashGenerator.description')}
        </p>

      {/* Controls */}
      <div className="card mb-6">
        <div className="space-y-4">
          {/* Input Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('toolPages.hashGenerator.inputType')}
            </label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 w-fit">
              <button
                onClick={() => setInputType('text')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  inputType === 'text'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t('toolPages.hashGenerator.text')}
              </button>
              <button
                onClick={() => setInputType('file')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  inputType === 'file'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {t('toolPages.hashGenerator.file')}
              </button>
            </div>
          </div>

          {/* Algorithm Selection */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                {t('toolPages.hashGenerator.algorithm')}
              </label>
              <button onClick={selectAllAlgorithms} className="text-xs text-primary-600 hover:text-primary-700">
                {t('toolPages.hashGenerator.selectAll')}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {algorithms.map((algo) => (
                <button
                  key={algo.value}
                  onClick={() => toggleAlgorithm(algo.value)}
                  className={`px-3 py-2 text-sm rounded-lg transition-colors ${
                    selectedAlgorithms.includes(algo.value)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {algo.label}
                  <span className="ml-1 text-xs opacity-70">({algo.bits}bit)</span>
                </button>
              ))}
            </div>
          </div>

          {/* Output Format */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('toolPages.hashGenerator.outputFormat')}
            </label>
            <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600 w-fit">
              <button
                onClick={() => handleFormatChange('hex')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  outputFormat === 'hex'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Hexadecimal
              </button>
              <button
                onClick={() => handleFormatChange('base64')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  outputFormat === 'base64'
                    ? 'bg-primary-600 text-white'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                Base64
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">{t('common.input')}</h2>
            <button onClick={handleClear} className="btn-secondary text-sm">
              {t('common.clear')}
            </button>
          </div>

          {inputType === 'text' ? (
            <textarea
              value={input}
              onChange={(e) => handleTextChange(e.target.value)}
              placeholder={t('toolPages.hashGenerator.inputPlaceholder')}
              className="textarea h-64 scrollbar-thin"
            />
          ) : (
            <div className="space-y-4">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="input"
              />
              {fileName && (
                <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    📄 {t('toolPages.hashGenerator.selectedFile')}: <span className="font-medium">{fileName}</span>
                  </p>
                </div>
              )}
              {isProcessing && (
                <div className="flex items-center gap-2 text-primary-600">
                  <span className="animate-spin">⏳</span>
                  <span>{t('toolPages.hashGenerator.processing')}</span>
                </div>
              )}
            </div>
          )}

          {inputType === 'text' && input && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {input.length} {t('toolPages.hashGenerator.characters')} / {new Blob([input]).size} {t('toolPages.hashGenerator.bytes')}
            </p>
          )}
        </div>

        {/* Output Section */}
        <div className="card">
          <div className="flex justify-between items-center mb-3">
            <h2 className="section-title">{t('toolPages.hashGenerator.results')}</h2>
            {results.length > 0 && (
              <CopyButton text={getAllHashesText()} />
            )}
          </div>

          {results.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400">
              {selectedAlgorithms.length === 0 
                ? t('toolPages.hashGenerator.selectAlgorithm')
                : t('toolPages.hashGenerator.enterTextOrFile')}
            </p>
          ) : (
            <div className="space-y-4">
              {results.map((result) => (
                <div key={result.algorithm} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {result.algorithm}
                    </span>
                    <CopyButton text={result.hash} className="text-xs" />
                  </div>
                  <code className="block font-mono text-xs text-gray-800 dark:text-gray-200 break-all">
                    {result.hash}
                  </code>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {result.hash.length} {t('toolPages.hashGenerator.characters')}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Hash Comparison */}
      <div className="mt-6 card">
        <h2 className="section-title mb-3">{t('toolPages.hashGenerator.hashComparison')}</h2>
        <HashComparison />
      </div>

      {/* Info Section */}
      <div className="mt-8 card bg-gray-50 dark:bg-gray-800/50">
        <h3 className="font-semibold mb-2">💡 해시 함수란?</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          해시 함수는 임의 크기의 데이터를 고정 크기의 값으로 변환하는 단방향 함수입니다.
          동일한 입력은 항상 동일한 출력을 생성하지만, 출력에서 입력을 역산할 수 없습니다.
        </p>
        <h4 className="font-medium text-sm mb-1">알고리즘별 특징:</h4>
        <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
          <li><strong>MD5</strong>: 128비트, 빠르지만 보안 용도로는 권장하지 않음</li>
          <li><strong>SHA-1</strong>: 160비트, MD5보다 안전하지만 역시 취약점 발견됨</li>
          <li><strong>SHA-256</strong>: 256비트, 현재 가장 널리 사용되는 안전한 알고리즘</li>
          <li><strong>SHA-512</strong>: 512비트, SHA-256보다 긴 출력</li>
          <li><strong>SHA-3</strong>: 최신 표준, Keccak 알고리즘 기반</li>
        </ul>
      </div>
      </div>
    </Layout>
  )
}

// Hash Comparison Component
function HashComparison() {
  const { t } = useTranslation()
  const [hash1, setHash1] = useState('')
  const [hash2, setHash2] = useState('')
  const [result, setResult] = useState<boolean | null>(null)

  const compare = () => {
    if (!hash1.trim() || !hash2.trim()) {
      setResult(null)
      return
    }
    setResult(hash1.trim().toLowerCase() === hash2.trim().toLowerCase())
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('toolPages.hashGenerator.hash1')}
          </label>
          <input
            type="text"
            value={hash1}
            onChange={(e) => {
              setHash1(e.target.value)
              setResult(null)
            }}
            placeholder={t('toolPages.hashGenerator.hash1Placeholder')}
            className="input font-mono text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('toolPages.hashGenerator.hash2')}
          </label>
          <input
            type="text"
            value={hash2}
            onChange={(e) => {
              setHash2(e.target.value)
              setResult(null)
            }}
            placeholder={t('toolPages.hashGenerator.hash2Placeholder')}
            className="input font-mono text-sm"
          />
        </div>
      </div>
      <div className="flex items-center gap-4">
        <button onClick={compare} className="btn-primary">
          {t('toolPages.hashGenerator.compare')}
        </button>
        {result !== null && (
          <div className={`px-4 py-2 rounded-lg ${
            result 
              ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
          }`}>
            {result ? `✅ ${t('toolPages.hashGenerator.match')}` : `❌ ${t('toolPages.hashGenerator.noMatch')}`}
          </div>
        )}
      </div>
    </div>
  )
}

export default HashGenerator
